import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { isAuthenticatedViaCookie, getCookie, getUserInfoFromLocalStorage } from '../../utils/utils';
import {
  fetchSubscriptionsApi,
  pauseSubscriptionApi,
  resumeSubscriptionApi,
  cancelSubscriptionApi,
  fetchPatientRecurringSchedulesApi,
  pausePatientRecurringScheduleApi,
  resumePatientRecurringScheduleApi,
  cancelPatientRecurringScheduleApi
} from '../../utils/apis';
import LoadingOverlay from '../insurance/LoadingOverlay';
import Sidebar from '../linkpayment/Sidebar';
import MobileMenuButton from '../linkpayment/MobileMenuButton';
import { InPortalLayoutContext } from '../linkpayment/PortalLayout';
import '../linkpayment/LinkPayment.css';

interface Subscription {
  subscriptionId: string;
  amount: number;
  frequency: string;
  startDate: string;
  endDate: string;
  status: string;
  isPaused: boolean | null;
  isActive: boolean;
  nextPaymentDate: string;
  lastPaymentDate: string | null;
  totalPayments: number | null;
  createdAt: string;
  updatedAt: string;
  metadata: any;
  success: boolean | null;
  message: string | null;
  error: string | null;
  isCancelled: boolean | null;
  source?: 'PAYABLI' | 'CUSTOM'; // PAYABLI = /api/patient/subscriptions; CUSTOM = /api/patient/recurring-schedules
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const Subscriptions: React.FC = () => {
  const { isAuthenticated } = useAuthContext();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const inLayout = useContext(InPortalLayoutContext);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    action: 'pause' | 'cancel' | null;
    subscriptionId: string | null;
    source: 'PAYABLI' | 'CUSTOM' | null;
  }>({
    isOpen: false,
    action: null,
    subscriptionId: null,
    source: null
  });

  // Check authentication using cookie
  const isUserAuthenticated = (): boolean => {
    const cookieAuth = isAuthenticatedViaCookie();
    const localAuth = localStorage.getItem('isAuthenticated') === 'true';
    const contextAuth = isAuthenticated;
    return cookieAuth || localAuth || contextAuth;
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    const authStatus = isUserAuthenticated();
    
    if (!authStatus) {
      navigate('/');
      return;
    }

    // Get user info from local storage
    setUserInfo(getUserInfoFromLocalStorage());

    fetchSubscriptions();
  }, [isAuthenticated, navigate]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Merge both recurring systems into one list: Payabli-native subscriptions and the in-house
      // custom-dates schedules. allSettled so one source failing still shows the other.
      const [payabliRes, customRes] = await Promise.allSettled([
        fetchSubscriptionsApi(),
        fetchPatientRecurringSchedulesApi(),
      ]);

      const payabli: Subscription[] =
        payabliRes.status === 'fulfilled' && Array.isArray(payabliRes.value)
          ? payabliRes.value.map((s: any) => ({ ...s, source: 'PAYABLI' as const }))
          : [];

      const custom: Subscription[] =
        customRes.status === 'fulfilled' && Array.isArray(customRes.value)
          ? customRes.value.map((s: any): Subscription => ({
              subscriptionId: String(s.id),
              amount: s.amount,
              frequency: s.cadence || s.frequency || '',
              startDate: s.startDate,
              endDate: s.endDate,
              status: s.status,
              isPaused: s.status === 'PAUSED',
              isActive: s.status === 'ACTIVE',
              nextPaymentDate: s.nextChargeDate,
              lastPaymentDate: null,
              totalPayments: null,
              createdAt: s.createdAt,
              updatedAt: s.updatedAt,
              metadata: s.metadata,
              success: null,
              message: null,
              error: null,
              isCancelled: s.status === 'CANCELLED',
              source: 'CUSTOM',
            }))
          : [];

      if (payabliRes.status === 'rejected' && customRes.status === 'rejected') {
        // Both rails failed to load — surface an error instead of an empty list, which would wrongly
        // imply the patient has no plans and invite a duplicate enrollment.
        setError("We couldn't load your subscriptions right now. Please refresh and try again.");
      }

      setSubscriptions([...payabli, ...custom]);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch subscriptions');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (
    subscriptionId: string,
    action: 'pause' | 'resume' | 'cancel',
    source?: 'PAYABLI' | 'CUSTOM'
  ) => {
    try {
      setActionLoading(subscriptionId);
      setError(null);

      // Route by the row's source, passed explicitly so a Payabli id that happens to equal a custom
      // schedule id can never misroute; fall back to a lookup only if a caller omits it. Custom-dates
      // schedules hit /api/patient/recurring-schedules, Payabli-native subs hit /api/patient/subscriptions.
      const isCustom = source
        ? source === 'CUSTOM'
        : subscriptions.find(s => s.subscriptionId === subscriptionId)?.source === 'CUSTOM';

      let response;
      switch (action) {
        case 'pause':
          response = isCustom
            ? await pausePatientRecurringScheduleApi(subscriptionId)
            : await pauseSubscriptionApi(subscriptionId);
          break;
        case 'resume':
          response = isCustom
            ? await resumePatientRecurringScheduleApi(subscriptionId)
            : await resumeSubscriptionApi(subscriptionId);
          break;
        case 'cancel':
          response = isCustom
            ? await cancelPatientRecurringScheduleApi(subscriptionId)
            : await cancelSubscriptionApi(subscriptionId);
          break;
      }

      // Payabli returns {success:true}; the custom endpoint returns the updated schedule object.
      const ok = isCustom ? !!response : (response && response.success);
      if (ok) {
        // Refresh the subscriptions list
        fetchSubscriptions();
      } else {
        setError(`Failed to ${action} subscription`);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to ${action} subscription`);
    } finally {
      setActionLoading(null);
    }
  };

  const openConfirmationModal = (action: 'pause' | 'cancel', subscriptionId: string, source?: 'PAYABLI' | 'CUSTOM') => {
    setConfirmationModal({
      isOpen: true,
      action,
      subscriptionId,
      source: source ?? null
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({
      isOpen: false,
      action: null,
      subscriptionId: null,
      source: null
    });
  };

  const confirmAction = () => {
    if (confirmationModal.action && confirmationModal.subscriptionId) {
      handleAction(confirmationModal.subscriptionId, confirmationModal.action, confirmationModal.source ?? undefined);
      closeConfirmationModal();
    }
  };

  const getStatusColor = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'paused':
        return 'text-orange-600 bg-orange-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getActionButton = (subscription: Subscription) => {
    const isLoading = actionLoading === subscription.subscriptionId;
    
    // Terminal states have no valid actions. Custom schedules also reach COMPLETED once all their
    // installments run — show a label, not Resume/Cancel buttons that would 400.
    if (subscription.isCancelled || subscription.status === 'COMPLETED') {
      return <span className="text-gray-500 text-sm">{subscription.isCancelled ? 'Cancelled' : 'Completed'}</span>;
    }
    
    // If isActive is false, subscription is paused - show resume option
    if (!subscription.isActive) {
      return (
        <div className="flex gap-2">
          <button
            onClick={() => handleAction(subscription.subscriptionId, 'resume', subscription.source)}
            disabled={isLoading}
            className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
          >
            {isLoading ? 'Resuming...' : 'Resume'}
          </button>
          <button
            onClick={() => openConfirmationModal('cancel', subscription.subscriptionId, subscription.source)}
            disabled={isLoading}
            className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      );
    }
    
    // If isActive is true, subscription is active - show pause and cancel options
    return (
      <div className="flex gap-2">
        <button
          onClick={() => openConfirmationModal('pause', subscription.subscriptionId, subscription.source)}
          disabled={isLoading}
          className="px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
        >
          Pause
        </button>
        <button
          onClick={() => openConfirmationModal('cancel', subscription.subscriptionId, subscription.source)}
          disabled={isLoading}
          className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getConfirmationModalProps = () => {
    if (!confirmationModal.action || !confirmationModal.subscriptionId) return null;

    const subscription = subscriptions.find(
      s => s.subscriptionId === confirmationModal.subscriptionId && s.source === confirmationModal.source
    );
    const amount = subscription ? formatAmount(subscription.amount) : '';

    if (confirmationModal.action === 'pause') {
      return {
        title: 'Pause Subscription',
        message: `Are you sure you want to pause this subscription? The subscription will be paused and no further payments will be processed until you resume it.`,
        confirmText: 'Pause Subscription',
        cancelText: 'Keep Active'
      };
    } else if (confirmationModal.action === 'cancel') {
      return {
        title: 'Cancel Subscription',
        message: `Are you sure you want to cancel this subscription? This action cannot be undone and the subscription will be permanently cancelled.`,
        confirmText: 'Cancel Subscription',
        cancelText: 'Keep Subscription'
      };
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Sidebar + mobile header — provided by PortalLayout when nested */}
        {!inLayout && (
        <>
        <Sidebar
          userInfo={userInfo}
          isOpen={isSidebarOpen}
          onClose={closeSidebar}
        />

        <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
          <div className="px-4 py-3 flex items-center justify-between flex-row-reverse">
            <div className="flex items-center">
              <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
            </div>
            <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
          </div>
        </div>
        </>
        )}

        <div className="w-full">
          <LoadingOverlay />
        </div>
      </div>
    );
  }

  // Client-side pagination — the subscriptions API returns the full list (no server paging).
  const PAGE_SIZE = 10;
  const totalPages = Math.max(1, Math.ceil(subscriptions.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages - 1);
  const pagedSubscriptions = subscriptions.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      {/* Sidebar + mobile header — provided by PortalLayout when nested */}
      {!inLayout && (
      <>
      <Sidebar
        userInfo={userInfo}
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
      />

      <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between flex-row-reverse">
          <div className="flex items-center">
            <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
          </div>
          <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
        </div>
      </div>
      </>
      )}

      <div className="w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Subscriptions</h2>
            <div className="text-sm text-gray-600">
              Total: {subscriptions.length} subscription{subscriptions.length !== 1 ? 's' : ''}
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {subscriptions.length === 0 && !error ? (
            <div className="text-center py-12">
              <div className="mb-6">
                <svg 
                  className="mx-auto h-16 w-16 text-gray-400" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor" 
                  aria-hidden="true"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" 
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Subscriptions</h3>
              <p className="text-gray-500 mb-6">You don't have any active subscriptions yet. Start by creating your first subscription!</p>
              <button
                onClick={() => navigate('/payments')}
                className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${theme["button-bg"]} hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                Create Your First Subscription
                <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {pagedSubscriptions.map((subscription) => (
                <div key={`${subscription.source}-${subscription.subscriptionId}`} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {formatAmount(subscription.amount)}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription.status)}`}>
                          {subscription.status}
                        </span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {subscription.frequency}
                        </span>
                        {subscription.isPaused && (
                          <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                            Paused
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Subscription ID:</span>
                          <div className="mt-1 font-mono text-xs text-gray-900 break-all">
                            #{subscription.subscriptionId}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Started:</span>
                          <div className="mt-1 text-gray-900">
                            {formatDate(subscription.startDate)}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Next Payment:</span>
                          <div className="mt-1 text-gray-900">
                            {subscription.nextPaymentDate ? formatDate(subscription.nextPaymentDate) : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Total Payments:</span>
                          <div className="mt-1 text-gray-900">
                            {subscription.totalPayments || 0}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          per {(subscription.frequency || '').toLowerCase()}
                        </div>
                        {getActionButton(subscription)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {subscriptions.length > PAGE_SIZE && (
                <div className="flex items-center justify-between pt-4 mt-2 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(Math.max(safePage - 1, 0))}
                    disabled={safePage === 0}
                    className="px-4 py-2 bg-gray-700 text-white rounded disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <div className="text-sm text-gray-700">
                    Page {safePage + 1} of {totalPages}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(Math.min(safePage + 1, totalPages - 1))}
                    disabled={safePage >= totalPages - 1}
                    className="px-4 py-2 bg-gray-700 text-white rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>

      {getConfirmationModalProps() && (
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          onClose={closeConfirmationModal}
          onConfirm={confirmAction}
          title={getConfirmationModalProps()!.title}
          message={getConfirmationModalProps()!.message}
          confirmText={getConfirmationModalProps()!.confirmText}
          cancelText={getConfirmationModalProps()!.cancelText}
        />
      )}
    </div>
  );
};

export default Subscriptions; 